/**
 * Lightweight FPS / frame-time overlay for perf debugging.
 * Enable via pause "Show FPS", `?fps=1`, or localStorage `zv_show_fps=1`.
 */

const STORAGE_KEY = "zv_show_fps";
/** Fallback when localStorage is unavailable (e.g. Vitest node). */
let memoryShowFps = false;

export function readShowFpsPreference(): boolean {
  try {
    if (typeof location !== "undefined" && typeof location.search === "string") {
      const params = new URLSearchParams(location.search);
      if (params.get("fps") === "1" || params.get("fps") === "true") return true;
      if (params.get("fps") === "0" || params.get("fps") === "false") return false;
    }
  } catch {
    // ignore
  }
  try {
    if (typeof localStorage !== "undefined") {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "1") return true;
      if (stored === "0") return false;
    }
  } catch {
    // ignore
  }
  return memoryShowFps;
}

export function writeShowFpsPreference(on: boolean): void {
  memoryShowFps = on;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
    }
  } catch {
    // ignore quota / private mode
  }
}

export class FpsOverlay {
  private readonly root: HTMLElement;
  private readonly fpsEl: HTMLElement;
  private readonly msEl: HTMLElement;
  private enabled = false;
  private frames = 0;
  private lastSampleMs = 0;
  private fps = 0;
  private frameMs = 0;
  private lastHudMs = 0;

  public constructor(host: HTMLElement) {
    this.root = document.createElement("div");
    this.root.className = "fps-overlay";
    this.root.hidden = true;
    this.root.setAttribute("aria-hidden", "true");
    this.root.innerHTML = `<span data-fps>0</span><span data-ms>0.0ms</span>`;
    this.fpsEl = this.root.querySelector("[data-fps]")!;
    this.msEl = this.root.querySelector("[data-ms]")!;
    host.appendChild(this.root);
    this.setEnabled(readShowFpsPreference());
  }

  public setEnabled(on: boolean): void {
    this.enabled = on;
    this.root.hidden = !on;
    if (on) {
      this.frames = 0;
      this.lastSampleMs = performance.now();
      this.lastHudMs = 0;
    }
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public tick(): void {
    if (!this.enabled) return;
    const now = performance.now();
    this.frames += 1;
    if (this.lastSampleMs === 0) {
      this.lastSampleMs = now;
      return;
    }
    const elapsed = now - this.lastSampleMs;
    if (elapsed >= 400) {
      this.fps = Math.round((this.frames * 1000) / elapsed);
      this.frameMs = elapsed / this.frames;
      this.frames = 0;
      this.lastSampleMs = now;
      if (now - this.lastHudMs >= 200) {
        this.fpsEl.textContent = String(this.fps);
        this.msEl.textContent = `${this.frameMs.toFixed(1)}ms`;
        this.lastHudMs = now;
      }
    }
  }

  public dispose(): void {
    this.root.remove();
  }
}
